import { useMemo, useState } from 'react';
import { TablePagination } from '../components/TablePagination';
import {
  Card, Tabs, Select, MultiSelect, NumberInput, TextInput, Button, Group, Stack, Table,
  Text, Badge, Center, Modal, Switch, ActionIcon, Loader, } from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconPencil, IconTrash, IconMoodSmile, IconStar, IconArrowsSort } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useClasses } from '../hooks/useLookups';
import { FetchOverlay } from '../components/FetchOverlay';
import { EmptyState } from '../components/EmptyState';
import { TableSkeleton } from '../components/TableSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { ServerSearchSelect } from '../components/ServerSearchSelect';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete } from '../lib/confirm';
import { formatDate } from '../lib/format';

/** Mezonlarni faqat rahbariyat tahrirlaydi (o'qituvchi ko'radi, lekin o'zgartira olmaydi). */
const MANAGER_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION'];

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

interface Criterion { id: string; name: string; points: number; isActive: boolean; order: number; createdAt: string }
interface StudentOpt { id: string; fullName: string }
interface BehaviorScore {
  id: string;
  studentId: string;
  value: number;
  note: string | null;
  date: string;
  student: { id: string; fullName: string; class: { name: string } | null };
  criterion: { id: string; name: string; points: number } | null;
  createdBy: { id: string; fullName: string } | null;
}

/**
 * Xulq baholari — ikki bo'lim:
 *  • "Baho qo'yish" — sinf → o'quvchilar (bir vaqtda 60 tagacha) → mezon/ball;
 *  • "Mezonlar" — xulq mezonlari CRUD (faqat rahbariyat).
 */
export default function Behavior() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role ?? '');

  // — Baho qo'yish holati
  const [classId, setClassId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [criterionId, setCriterionId] = useState<string | null>(null);
  const [value, setValue] = useState<string | number>('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState<Date | null>(null);

  // — Oxirgi baholar ro'yxati filtrlari (baho qo'yish formasidan MUSTAQIL:
  //   ro'yxatni sinf tanlamay ham ko'rish mumkin)
  const [fClassId, setFClassId] = useState<string | null>(null);
  const [fStudentId, setFStudentId] = useState<string | null>(null);
  const [fCriterionId, setFCriterionId] = useState<string | null>(null);
  const [fSign, setFSign] = useState<string | null>(null);
  const [fRange, setFRange] = useState<[Date | null, Date | null]>([null, null]);
  const [fSort, setFSort] = useState('date:desc');

  // — Mezonlar holati
  const [showArchived, setShowArchived] = useState(false);
  const [criteriaSort, setCriteriaSort] = useState('order'); // mijoz tomonda saralash
  const [editing, setEditing] = useState<Criterion | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const { data: classes } = useClasses();

  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['students-of-class', classId],
    queryFn: async () => (await api.get<StudentOpt[]>('/students', { params: { classId } })).data,
    enabled: !!classId,
  });

  // Baho qo'yish uchun — faqat FAOL mezonlar (arxivlanganlar tanlanmaydi)
  const { data: activeCriteria } = useQuery({
    queryKey: ['behavior-criteria', 'active'],
    queryFn: async () => (await api.get<Criterion[]>('/ranking/behavior-criteria')).data,
  });

  // Mezonlar jadvali — `all=1` bo'lsa arxivlanganlar ham keladi
  const { data: criteria, isLoading: criteriaLoading, isFetching: criteriaFetching } = useQuery({
    queryKey: ['behavior-criteria', showArchived],
    queryFn: async () =>
      (await api.get<Criterion[]>('/ranking/behavior-criteria', { params: showArchived ? { all: 1 } : undefined })).data,
  });

  // Lokal sana → YYYY-MM-DD. toISOString() EMAS: UTC+5 da lokal yarim tun
  // bir kun orqaga siljib, noto'g'ri oraliq so'ralardi.
  const iso = (d: Date | null) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

  const {
    items: scores, isLoading: scoresLoading, fetching: scoresFetching,
    pages: scorePages, page: scorePage, setPage: setScorePage,
    total: scoreTotal, limit: scoreLimit, setLimit: setScoreLimit,
  } = usePagedQuery<BehaviorScore>('behavior-scores', '/ranking/behavior', {
    classId: fClassId || undefined,
    studentId: fStudentId || undefined,
    criterionId: fCriterionId || undefined,
    sign: fSign || undefined,
    from: iso(fRange[0]),
    to: iso(fRange[1]),
    sort: fSort,
  }, 20);

  const invalidateScores = () => {
    qc.invalidateQueries({ queryKey: ['behavior-scores'] });
    qc.invalidateQueries({ queryKey: ['ranking'] });
  };
  // ['behavior-criteria', ...] prefiksi — faol ro'yxat ham, jadval ham yangilanadi
  const invalidateCriteria = () => qc.invalidateQueries({ queryKey: ['behavior-criteria'] });

  const hasValue = value !== '' && value !== null && !Number.isNaN(Number(value));

  const scoreMut = useMutation({
    mutationFn: () =>
      api.post('/ranking/behavior', {
        studentIds: selected,
        criterionId: criterionId || undefined,
        value: hasValue ? Number(value) : undefined,
        note: note.trim() || undefined,
        date: date ? date.toISOString() : undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('behaviorPage.saved') });
      setSelected([]); setNote(''); setValue(''); setCriterionId(null);
      invalidateScores();
    },
    onError: (e) => notifications.show({ color: 'red', message: errMsg(e, t('behaviorPage.saveError')) }),
  });

  const delScoreMut = useMutation({
    mutationFn: (id: string) => api.delete(`/ranking/behavior/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('behaviorPage.deleted') });
      invalidateScores();
    },
    onError: (e) => notifications.show({ color: 'red', message: errMsg(e, t('behaviorPage.saveError')) }),
  });

  const submit = () => {
    if (!selected.length) {
      notifications.show({ color: 'red', message: t('behaviorPage.studentsRequired') });
      return;
    }
    if (!criterionId && !hasValue) {
      notifications.show({ color: 'red', message: t('behaviorPage.criterionRequired') });
      return;
    }
    scoreMut.mutate();
  };

  // — Mezon formasi (qo'shish/tahrirlash)
  const form = useForm({
    initialValues: { name: '', points: 1 as string | number, order: 0 as string | number, isActive: true },
    validate: {
      name: (v) => (!v.trim() ? t('behaviorPage.nameRequired') : null),
      points: (v) =>
        v === '' || Number.isNaN(Number(v)) || Number(v) < -10 || Number(v) > 10
          ? t('behaviorPage.pointsRange')
          : null,
    },
  });

  const saveCriterionMut = useMutation({
    mutationFn: (v: typeof form.values) => {
      const body = {
        name: v.name.trim(),
        points: Number(v.points),
        order: Number(v.order) || 0,
        isActive: v.isActive,
      };
      return editing
        ? api.patch(`/ranking/behavior-criteria/${editing.id}`, body)
        : api.post('/ranking/behavior-criteria', body);
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('behaviorPage.criterionSaved') });
      invalidateCriteria();
      form.reset(); setEditing(null); close();
    },
    onError: (e) => notifications.show({ color: 'red', message: errMsg(e, t('behaviorPage.saveError')) }),
  });

  const delCriterionMut = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean; archived: boolean }>(`/ranking/behavior-criteria/${id}`),
    onSuccess: (res) => {
      notifications.show(
        res.data?.archived
          ? { color: 'yellow', message: t('behaviorPage.criterionArchived') }
          : { color: 'green', message: t('behaviorPage.criterionDeleted') }
      );
      invalidateCriteria();
    },
    onError: (e) => notifications.show({ color: 'red', message: errMsg(e, t('behaviorPage.saveError')) }),
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ name: '', points: 1, order: 0, isActive: true });
    form.clearErrors();
    open();
  };
  const openEdit = (c: Criterion) => {
    setEditing(c);
    form.setValues({ name: c.name, points: c.points, order: c.order, isActive: c.isActive });
    form.clearErrors();
    open();
  };

  const studentOpts = (students ?? []).map((s) => ({ value: s.id, label: s.fullName }));

  // Mezonlar jadvali qisqa (10–30 qator) — saralash MIJOZ tomonda, sahifalashsiz.
  const sortedCriteria = useMemo(() => {
    const list = criteria ?? [];
    if (criteriaSort === 'points:desc') return [...list].sort((a, b) => b.points - a.points);
    if (criteriaSort === 'points:asc') return [...list].sort((a, b) => a.points - b.points);
    return list; // serverdagi tartib: order → createdAt
  }, [criteria, criteriaSort]);

  return (
    <>
      <PageHeader title={t('pages.behavior.title')} />

      <Tabs defaultValue="grade">
        <Tabs.List mb="md">
          <Tabs.Tab value="grade" leftSection={<IconStar size={16} />}>{t('behaviorPage.tabGrade')}</Tabs.Tab>
          <Tabs.Tab value="criteria" leftSection={<IconMoodSmile size={16} />}>{t('behaviorPage.tabCriteria')}</Tabs.Tab>
        </Tabs.List>

        {/* ——— Baho qo'yish ——— */}
        <Tabs.Panel value="grade">
          <Card mb="md">
            <Select
              label={t('behaviorPage.selectClass')}
              placeholder={t('common.selectPlaceholder')}
              data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={classId}
              onChange={(v) => { setClassId(v); setSelected([]); }}
              searchable
              w={220}
            />

            {!classId ? (
              <Center h={140}><Text c="dimmed">{t('behaviorPage.selectClassFirst')}</Text></Center>
            ) : studentsLoading ? (
              <Center h={140}><Loader /></Center>
            ) : (
              <Stack mt="md">
                <MultiSelect
                  label={t('behaviorPage.students')}
                  placeholder={t('behaviorPage.selectStudents')}
                  searchable
                  data={studentOpts}
                  value={selected}
                  onChange={setSelected}
                />
                <Group gap="xs">
                  <Button size="xs" variant="light" onClick={() => setSelected(studentOpts.map((o) => o.value))}>
                    {t('behaviorPage.selectAll')}
                  </Button>
                  <Button size="xs" variant="light" onClick={() => setSelected([])}>
                    {t('behaviorPage.clearAll')}
                  </Button>
                </Group>

                <Group grow align="flex-start">
                  <Select
                    label={t('behaviorPage.criterion')}
                    placeholder={t('common.selectPlaceholder')}
                    data={(activeCriteria ?? []).map((c) => ({
                      value: c.id,
                      label: `${c.name} (${c.points > 0 ? '+' : ''}${c.points})`,
                    }))}
                    value={criterionId}
                    onChange={setCriterionId}
                    clearable
                    searchable
                  />
                  <NumberInput
                    label={t('behaviorPage.value')}
                    description={t('behaviorPage.valueHint')}
                    min={-10}
                    max={10}
                    allowNegative
                    value={value}
                    onChange={setValue}
                  />
                </Group>

                <Group grow align="flex-start">
                  <TextInput
                    label={t('behaviorPage.note')}
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                  />
                  <DateInput
                    label={t('behaviorPage.date')}
                    valueFormat="DD.MM.YYYY"
                    clearable
                    value={date}
                    onChange={setDate}
                  />
                </Group>

                <Group justify="flex-end">
                  <Button onClick={submit} loading={scoreMut.isPending}>{t('behaviorPage.submit')}</Button>
                </Group>
              </Stack>
            )}
          </Card>

          <Text fw={600} mb="sm">{t('behaviorPage.recent')}</Text>

          {/*
            TableToolbar qolipi, LEKIN qidiruv maydonisiz: GET /ranking/behavior `q`
            parametrini qo'llamaydi (o'quvchi bo'yicha qidiruv — ServerSearchSelect orqali).
          */}
          <Group mb="md" gap="sm" align="flex-end" wrap="wrap">
            <Select
              placeholder={t('behaviorPage.allClasses')}
              data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={fClassId || null}
              onChange={setFClassId}
              clearable searchable w={160}
            />
            <ServerSearchSelect
              placeholder={t('behaviorPage.allStudents')}
              endpoint="/students"
              mapOption={(st: { id: string; fullName: string; class?: { name: string } | null }) =>
                ({ value: st.id, label: `${st.fullName}${st.class ? ` · ${st.class.name}` : ''}` })}
              value={fStudentId || null}
              onChange={setFStudentId}
              clearable w={190}
            />
            <Select
              placeholder={t('behaviorPage.allCriteria')}
              data={(activeCriteria ?? []).map((c) => ({
                value: c.id,
                label: `${c.name} (${c.points > 0 ? '+' : ''}${c.points})`,
              }))}
              value={fCriterionId || null}
              onChange={setFCriterionId}
              clearable searchable w={190}
            />
            <Select
              placeholder={t('behaviorPage.allSigns')}
              data={[
                { value: 'pos', label: t('behaviorPage.positive') },
                { value: 'neg', label: t('behaviorPage.negative') },
              ]}
              value={fSign || null}
              onChange={setFSign}
              clearable w={150}
            />
            <DatePickerInput
              type="range"
              placeholder={t('behaviorPage.dateRange')}
              value={fRange}
              onChange={setFRange}
              valueFormat="DD.MM.YYYY"
              clearable w={190}
            />
            <Select
              leftSection={<IconArrowsSort size={16} />}
              data={[
                { value: 'date:desc', label: t('behaviorPage.sortDateDesc') },
                { value: 'date:asc', label: t('behaviorPage.sortDateAsc') },
                { value: 'value:desc', label: t('behaviorPage.sortValueDesc') },
                { value: 'value:asc', label: t('behaviorPage.sortValueAsc') },
              ]}
              value={fSort}
              onChange={(v) => v && setFSort(v)}
              allowDeselect={false}
              w={210}
            />
          </Group>

          <SectionCard title={t('common.list')} icon={IconStar} p={0} pos="relative">
            {scoresLoading ? (
              <TableSkeleton rows={6} cols={6} />
            ) : !scores.length ? (
              <EmptyState
                icon={IconStar}
                title={t('behaviorPage.noRecords')}
                description={t('common.noMatch')}
              />
            ) : (
              <Table.ScrollContainer minWidth={700}>
                <FetchOverlay visible={scoresFetching} />
                <Table highlightOnHover verticalSpacing="sm" stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('behaviorPage.colStudent')}</Table.Th>
                      <Table.Th>{t('behaviorPage.colClass')}</Table.Th>
                      <Table.Th>{t('behaviorPage.colCriterion')}</Table.Th>
                      <Table.Th>{t('behaviorPage.colValue')}</Table.Th>
                      <Table.Th>{t('behaviorPage.colNote')}</Table.Th>
                      <Table.Th>{t('behaviorPage.colDate')}</Table.Th>
                      <Table.Th>{t('behaviorPage.colBy')}</Table.Th>
                      <Table.Th w={60}>{t('common.actions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {scores.map((s) => (
                      <Table.Tr key={s.id}>
                        <Table.Td fw={500}>{s.student?.fullName ?? '—'}</Table.Td>
                        <Table.Td>{s.student?.class?.name ?? '—'}</Table.Td>
                        <Table.Td>{s.criterion?.name ?? t('behaviorPage.noCriterion')}</Table.Td>
                        <Table.Td>
                          <Badge color={s.value > 0 ? 'teal' : 'red'} variant="light">
                            {s.value > 0 ? `+${s.value}` : s.value}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{s.note || '—'}</Table.Td>
                        <Table.Td>{formatDate(s.date)}</Table.Td>
                        <Table.Td>{s.createdBy?.fullName ?? '—'}</Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() =>
                              confirmDelete(s.student?.fullName ?? t('behaviorPage.colStudent'), () => delScoreMut.mutate(s.id))
                            }
                           loading={delScoreMut.isPending}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </SectionCard>
          <TablePagination
            page={scorePage}
            pages={scorePages}
            total={scoreTotal}
            limit={scoreLimit}
            onPage={setScorePage}
            onLimit={setScoreLimit}
          />
        </Tabs.Panel>

        {/* ——— Mezonlar ——— */}
        <Tabs.Panel value="criteria">
          {/* Ichma-ich karta o'rniga yagona sarlavhali karta: boshqaruvlar header'ning o'ng tomonida */}
          <SectionCard
            title={t('behaviorPage.tabCriteria')}
            icon={IconMoodSmile}
            p={0}
            pos="relative"
            right={
              <Group gap="sm" wrap="nowrap">
                <Switch
                  label={t('behaviorPage.showArchived')}
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.currentTarget.checked)}
                />
                <Select
                  leftSection={<IconArrowsSort size={16} />}
                  data={[
                    { value: 'order', label: t('behaviorPage.sortOrder') },
                    { value: 'points:desc', label: t('behaviorPage.sortValueDesc') },
                    { value: 'points:asc', label: t('behaviorPage.sortValueAsc') },
                  ]}
                  value={criteriaSort}
                  onChange={(v) => v && setCriteriaSort(v)}
                  allowDeselect={false}
                  w={190}
                />
                {isManager && (
                  <Button leftSection={<IconPlus size={18} />} onClick={openCreate}>
                    {t('behaviorPage.addCriterion')}
                  </Button>
                )}
              </Group>
            }
          >
            <FetchOverlay visible={criteriaFetching} />
            {!isManager && <Text c="dimmed" fz="sm" px="lg" pt="sm">{t('behaviorPage.readOnlyHint')}</Text>}

            {criteriaLoading ? (
              <Center h={160}><Loader /></Center>
            ) : !criteria?.length ? (
              <Center h={140}><Text c="dimmed">{t('behaviorPage.noCriteria')}</Text></Center>
            ) : (
              <Table.ScrollContainer minWidth={620}>
                <Table highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('behaviorPage.criterionName')}</Table.Th>
                      <Table.Th>{t('behaviorPage.points')}</Table.Th>
                      <Table.Th>{t('behaviorPage.order')}</Table.Th>
                      <Table.Th>{t('behaviorPage.isActive')}</Table.Th>
                      <Table.Th w={100}>{t('common.actions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sortedCriteria.map((c) => (
                      <Table.Tr key={c.id}>
                        <Table.Td fw={500}>{c.name}</Table.Td>
                        <Table.Td>
                          <Badge color={c.points > 0 ? 'teal' : c.points < 0 ? 'red' : 'gray'} variant="light">
                            {c.points > 0 ? `+${c.points}` : c.points}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{c.order}</Table.Td>
                        <Table.Td>
                          <Badge color={c.isActive ? 'teal' : 'gray'} variant="light">
                            {t(c.isActive ? 'behaviorPage.active' : 'behaviorPage.inactive')}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {isManager ? (
                            <Group gap={6} wrap="nowrap">
                              <ActionIcon variant="subtle" onClick={() => openEdit(c)}>
                                <IconPencil size={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => confirmDelete(c.name, () => delCriterionMut.mutate(c.id))}
                               loading={delCriterionMut.isPending}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>
                          ) : (
                            <Text c="dimmed" fz="sm">{t('behaviorPage.readOnlyHint')}</Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </SectionCard>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={opened}
        onClose={() => { close(); setEditing(null); }}
        title={editing ? t('behaviorPage.editCriterion') : t('behaviorPage.addCriterion')}
        centered
      >
        <form onSubmit={form.onSubmit((v) => saveCriterionMut.mutate(v))}>
          <Stack>
            <TextInput
              label={t('behaviorPage.criterionName')}
              placeholder={t('behaviorPage.criterionNamePlaceholder')}
              required
              {...form.getInputProps('name')}
            />
            <NumberInput
              label={t('behaviorPage.points')}
              description={t('behaviorPage.pointsHint')}
              min={-10}
              max={10}
              allowNegative
              required
              {...form.getInputProps('points')}
            />
            <NumberInput label={t('behaviorPage.order')} min={0} {...form.getInputProps('order')} />
            <Switch
              label={t('behaviorPage.isActive')}
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => { close(); setEditing(null); }}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saveCriterionMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
